import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Card } from '../ui/card';
import { EmojiText } from '../ui/SlackEmoji';
import type { User } from '../../types';

interface KudosCardProps {
  fromUserName: string;
  fromUserPhoto?: string;
  toUserName: string;
  message: string;
  createdAt: string | Date;
  variant?: 'default' | 'compact';
  users?: User[];
}

/**
 * KudosCard Component
 * 
 * A reusable card component for displaying kudos/appreciation messages.
 * Features:
 * - Avatar of the sender
 * - Chat bubble-style message
 * - Automatic replacement of Slack user mentions with actual names
 * - Relative timestamp (e.g., "2 hours ago")
 * 
 * @example
 * <KudosCard
 *   fromUserName="John Doe"
 *   fromUserPhoto="https://..."
 *   toUserName="Jane Smith"
 *   message="<@U12345> great work on the project!"
 *   createdAt="2024-12-02T10:30:00Z"
 * />
 */
export const KudosCard: React.FC<KudosCardProps> = ({
  fromUserName,
  fromUserPhoto,
  toUserName,
  message,
  createdAt,
  variant = 'default',
  users = [],
}) => {
  const { i18n } = useTranslation();
  
  // Create a map of Slack user ID to display name
  const slackIdToName = React.useMemo(() => {
    const map = new Map<string, string>();
    users.forEach(user => {
      if (user.slackUserId) {
        map.set(user.slackUserId, user.name);
      }
    });
    return map;
  }, [users]);
  
  // Replace Slack user mentions with actual names (without @ symbol)
  const formatMessage = (msg: string, recipientName: string): string => {
    // Replace each <@USERID> with the actual user's name
    return msg.replace(/<@([A-Z0-9]+)>/g, (match, slackId) => {
      // Try to find the user by Slack ID
      const userName = slackIdToName.get(slackId);
      // Fallback to recipient name if not found
      return userName || recipientName;
    });
  };

  const formattedMessage = formatMessage(message, toUserName);
  
  // Format timestamp as date and time using current locale
  const formatDateTime = (date: string | Date): string => {
    const d = new Date(date);
    return d.toLocaleString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const formattedDate = formatDateTime(createdAt);
  
  // Get initials for avatar fallback
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (variant === 'compact') {
    return (
      <div className="flex gap-3 py-2">
        <Avatar className="h-8 w-8">
          {fromUserPhoto && <AvatarImage src={fromUserPhoto} alt={fromUserName} />}
          <AvatarFallback className="bg-primary/10 text-primary text-xs">
            {getInitials(fromUserName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-medium text-sm">{fromUserName}</span>
            <span className="text-xs text-muted-foreground">{formattedDate}</span>
          </div>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
            <EmojiText text={formattedMessage} emojiSize={16} />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 group items-start">
      {/* Avatar - larger and centered */}
      <Avatar className="h-12 w-12 flex-shrink-0 mt-1">
        {fromUserPhoto && <AvatarImage src={fromUserPhoto} alt={fromUserName} />}
        <AvatarFallback className="bg-primary/10 text-primary text-base">
          {getInitials(fromUserName)}
        </AvatarFallback>
      </Avatar>

      {/* Message Bubble */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-semibold text-sm">{fromUserName}</span>
          <span className="text-xs text-muted-foreground">{formattedDate}</span>
        </div>

        {/* Message Card - simple rounded bubble */}
        <Card className="p-3 bg-primary/5 border-primary/20 shadow-sm">
          <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
            <EmojiText text={formattedMessage} emojiSize={18} />
          </p>
        </Card>
      </div>
    </div>
  );
};
